// Acessos por função (perfis_acesso, por papel) + exceções por pessoa (acessos_perfil).
// Só a líder/admin editam. Cada pessoa só lê o perfil do seu papel e a sua própria exceção.
// A base de dados aplica as mesmas regras (função nivel_acesso) — isto serve para a app
// saber o que mostrar. Desenho completo: docs/DESENHO_ACESSOS_PROCESSOS.md
import { useState, useEffect } from "react";

const L = {
  sem: "Sem acesso", proprios: "Só os seus", negocios: "Ligados a negócios em curso",
  financiamento: "Com financiamento", aceites: "Só a proposta aceite", ver: "Vê todos (só leitura)",
  gerir: "Gere todos", com: "Com acesso", todos: "Todas", propria: "A sua", todas: "Todas",
};
const opc = (...ids) => ids.map(id => ({ id, label: L[id] }));

export const AREAS_ACESSO = [
  { grupo: "Comercial", id: "contactos", label: "Contactos", opcoes: opc("sem", "proprios", "negocios", "financiamento", "ver", "gerir") },
  { grupo: "Comercial", id: "angariacoes", label: "Angariações (completas)", opcoes: opc("sem", "proprios", "negocios", "financiamento", "ver", "gerir") },
  { grupo: "Comercial", id: "imoveis_equipa", label: "Imóveis da equipa (sem proprietários)", opcoes: opc("sem", "com") },
  { grupo: "Processos", id: "propostas", label: "Propostas", opcoes: opc("sem", "proprios", "aceites", "financiamento", "ver", "gerir") },
  { grupo: "Processos", id: "negocios", label: "Negócios, escrituras e concluídos", opcoes: opc("sem", "proprios", "financiamento", "ver", "gerir") },
  { grupo: "Processos", id: "cpcv", label: "CPCV", opcoes: opc("sem", "proprios", "financiamento", "ver", "gerir") },
  { grupo: "Processos", id: "financiamento", label: "Financiamento", opcoes: opc("sem", "proprios", "ver", "gerir") },
  { grupo: "Comissões", id: "comissoes_agencia", label: "Comissão da agência", opcoes: [{ id: "sem", label: L.sem }, { id: "proprios", label: "Dos seus negócios" }, { id: "negocios", label: "Negócios em curso" }, { id: "todos", label: "Todas" }] },
  { grupo: "Comissões", id: "comissoes_consultores", label: "Comissões dos consultores", opcoes: opc("sem", "proprios", "todos").map(o => o.id === "proprios" ? { ...o, label: "A sua" } : o) },
  { grupo: "Comissões", id: "comissoes_lider", label: "Parte da líder (7,5%)", opcoes: [{ id: "sem", label: L.sem }, { id: "ver", label: "Vê" }] },
  { grupo: "Comissões", id: "comissoes_financeira", label: "Parte da gestora financeira (20%)", opcoes: opc("sem", "propria", "todas") },
  { grupo: "Outros", id: "relatorios", label: "Relatórios", opcoes: [{ id: "sem", label: L.sem }, { id: "proprios", label: "Os seus números" }, { id: "ver", label: "Números da equipa" }] },
  { grupo: "Outros", id: "marketing", label: "Marketing", opcoes: opc("sem", "proprios", "gerir") },
];
export const labelNivel = (area, nivel) => {
  const a = AREAS_ACESSO.find(x => x.id === area);
  return a?.opcoes.find(o => o.id === nivel)?.label || L[nivel] || nivel || "—";
};

// Papéis que têm perfil de acesso (ordem das colunas na tabela de perfis)
export const PAPEIS_PERFIL = [
  { id: "consultor", label: "Consultor" },
  { id: "gestora_processual", label: "Gestora Processual" },
  { id: "gestora_financeira", label: "Gestora Financeira" },
  { id: "coordenadora_financeira", label: "Coord. Financeira" },
  { id: "coordenadora_processual", label: "Coord. Processual" },
  { id: "marketing", label: "Marketing" },
];

// Sem perfil nem exceção = comportamento de sempre
const PADRAO_SEM = ["relatorios", "imoveis_equipa", "comissoes_lider", "comissoes_financeira"];
export function nivelAcessoDe(acessos, area, isLider) {
  if (isLider) return ({ imoveis_equipa: "com", comissoes_agencia: "todos", comissoes_consultores: "todos", comissoes_lider: "ver", comissoes_financeira: "todas" })[area] || "gerir";
  const ex = acessos?.excecoes?.[area];
  if (ex) return ex;
  const pf = acessos?.perfil?.[area];
  if (pf) return pf;
  return PADRAO_SEM.includes(area) ? "sem" : "proprios";
}

// Itens do menu → área de acesso ("comissoes" é tratado à parte: aparece se vê algum tipo)
export const AREA_DO_MENU = {
  leads: "contactos",
  visitas: "contactos",
  propostas: "propostas",
  "angariações": "angariacoes",
  "processo-proposta": "propostas",
  "processo-cpcv": "cpcv",
  financiamento: "financiamento",
  "processo-escritura": "negocios",
  "processo-concluidos": "negocios",
  marketing: "marketing",
};

// Carrega exceção da pessoa + perfil do seu papel
export function useAcessos(supabase, profileId, papel) {
  const [acessos, setAcessos] = useState(null);
  useEffect(() => {
    if (!profileId) { setAcessos(null); return; }
    let ativo = true;
    Promise.all([
      supabase.from("acessos_perfil").select("acessos").eq("profile_id", profileId).maybeSingle(),
      papel ? supabase.from("perfis_acesso").select("acessos").eq("papel", papel).maybeSingle() : Promise.resolve({ data: null }),
    ]).then(([ex, pf]) => {
      if (ativo) setAcessos({ excecoes: ex.data?.acessos || {}, perfil: pf.data?.acessos || {} });
    });
    return () => { ativo = false; };
  }, [supabase, profileId, papel]);
  return acessos;
}

const SEL = { padding: "5px 8px", border: "1.5px solid #ece9e3", borderRadius: 7, fontSize: 12, fontFamily: "inherit", background: "#fff", minWidth: 150, maxWidth: "100%" };
const papelLabel = p => PAPEIS_PERFIL.find(x => x.id === p)?.label || p;

// ── Exceções de UMA pessoa (cartão em Consultores) ──
export function PainelAcessos({ supabase, pessoa, profile, showNotif }) {
  const [excecoes, setExcecoes] = useState(null);
  const [perfil, setPerfil] = useState({});
  const [saving, setSaving] = useState(false);
  const pessoaELider = pessoa.papel === "lider" || pessoa.papel === "admin";

  useEffect(() => {
    if (pessoaELider) return;
    Promise.all([
      supabase.from("acessos_perfil").select("acessos").eq("profile_id", pessoa.id).maybeSingle(),
      supabase.from("perfis_acesso").select("acessos").eq("papel", pessoa.papel).maybeSingle(),
    ]).then(([ex, pf]) => { setExcecoes(ex.data?.acessos || {}); setPerfil(pf.data?.acessos || {}); });
  }, [supabase, pessoa.id, pessoa.papel, pessoaELider]);

  if (pessoaELider) return <div style={{ fontSize: 12, color: "#6b7280" }}>Líder e Admin têm sempre acesso total.</div>;
  if (!excecoes) return <div style={{ fontSize: 12, color: "#9ca3af" }}>A carregar acessos...</div>;

  const guardar = async () => {
    setSaving(true);
    const { error } = await supabase.from("acessos_perfil").upsert({
      profile_id: pessoa.id, acessos: excecoes, atualizado_em: new Date().toISOString(), atualizado_por: profile?.id || null,
    });
    setSaving(false);
    if (error) { alert("Erro a guardar acessos: " + error.message); return; }
    showNotif(`Exceções de ${pessoa.nome} guardadas.`);
  };
  const nExcecoes = Object.keys(excecoes).length;

  return (
    <div>
      <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 8, lineHeight: 1.5 }}>
        Segue o perfil <b>{papelLabel(pessoa.papel)}</b>. Muda só o que for diferente para esta pessoa
        {nExcecoes > 0 && <> — <b>{nExcecoes} exceç{nExcecoes === 1 ? "ão" : "ões"}</b></>}.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {AREAS_ACESSO.map(a => {
          const doPerfil = perfil[a.id] || (PADRAO_SEM.includes(a.id) ? "sem" : "proprios");
          const temExcecao = !!excecoes[a.id];
          return (
            <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12.5, color: temExcecao ? "#b45309" : "#374151", fontWeight: temExcecao ? 700 : 400 }}>{a.label}</span>
              <select value={excecoes[a.id] || ""} onChange={e => setExcecoes(p => {
                const n = { ...p }; if (e.target.value) n[a.id] = e.target.value; else delete n[a.id]; return n;
              })} style={{ ...SEL, borderColor: temExcecao ? "#f59e0b" : "#ece9e3" }}>
                <option value="">Igual ao perfil ({labelNivel(a.id, doPerfil)})</option>
                {a.opcoes.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          );
        })}
      </div>
      <button onClick={guardar} disabled={saving} style={{ marginTop: 10, background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 7, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
        {saving ? "A guardar..." : "Guardar exceções"}
      </button>
    </div>
  );
}

// ── Perfis de acesso por função (tabela editável — só líder/admin) ──
export function PerfisAcesso({ supabase, profile, showNotif }) {
  const [perfis, setPerfis] = useState(null);
  const [alterados, setAlterados] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("perfis_acesso").select("papel, acessos").then(({ data }) => {
      const m = {}; (data || []).forEach(r => { m[r.papel] = r.acessos || {}; });
      setPerfis(m);
    });
  }, [supabase]);
  if (!perfis) return <div style={{ padding: 20, color: "#9ca3af" }}>A carregar perfis...</div>;

  const muda = (papel, area, valor) => {
    setPerfis(p => ({ ...p, [papel]: { ...(p[papel] || {}), [area]: valor } }));
    setAlterados(a => ({ ...a, [papel]: true }));
  };
  const guardar = async () => {
    const papeis = Object.keys(alterados);
    if (papeis.length === 0) return;
    if (!window.confirm(`Guardar os perfis alterados (${papeis.map(papelLabel).join(", ")})?\n\nA mudança aplica-se a todas as pessoas com esse papel (exceto nas exceções individuais).`)) return;
    setSaving(true);
    const { error } = await supabase.from("perfis_acesso").upsert(papeis.map(p => ({
      papel: p, acessos: perfis[p], atualizado_em: new Date().toISOString(), atualizado_por: profile?.id || null,
    })));
    setSaving(false);
    if (error) { alert("Erro a guardar perfis: " + error.message); return; }
    setAlterados({});
    showNotif("Perfis de acesso guardados.");
  };
  let grupoAnterior = null;

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #ece9e3", padding: 18, marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>🔐 Perfis de acesso por função</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Cada pessoa segue o perfil do seu papel. Exceções individuais: no cartão da pessoa → ▼ Gerir → Acessos. Líder e Admin têm sempre tudo.</div>
        </div>
        <button onClick={guardar} disabled={saving || Object.keys(alterados).length === 0} style={{ background: Object.keys(alterados).length ? "#0B1B3D" : "#e5e7eb", color: Object.keys(alterados).length ? "#fff" : "#9ca3af", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: Object.keys(alterados).length ? "pointer" : "default" }}>
          {saving ? "A guardar..." : `Guardar perfis${Object.keys(alterados).length ? ` (${Object.keys(alterados).length})` : ""}`}
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1050, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 6px", borderBottom: "2px solid #0B1B3D", position: "sticky", left: 0, background: "#fff" }}>Informação</th>
              {PAPEIS_PERFIL.map(p => <th key={p.id} style={{ textAlign: "left", padding: "8px 6px", borderBottom: "2px solid #0B1B3D", color: alterados[p.id] ? "#b45309" : "#0B1B3D" }}>{p.label}{alterados[p.id] ? " •" : ""}</th>)}
            </tr>
          </thead>
          <tbody>
            {AREAS_ACESSO.flatMap(a => {
              const linhas = [];
              if (a.grupo !== grupoAnterior) {
                grupoAnterior = a.grupo;
                linhas.push(<tr key={"g-" + a.grupo}><td colSpan={PAPEIS_PERFIL.length + 1} style={{ padding: "10px 6px 4px", fontSize: 10.5, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>{a.grupo}</td></tr>);
              }
              linhas.push(
                <tr key={a.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "6px", fontWeight: 600, color: "#374151", position: "sticky", left: 0, background: "#fff" }}>{a.label}</td>
                  {PAPEIS_PERFIL.map(p => (
                    <td key={p.id} style={{ padding: "4px 6px" }}>
                      <select value={perfis[p.id]?.[a.id] || (PADRAO_SEM.includes(a.id) ? "sem" : "proprios")} onChange={e => muda(p.id, a.id, e.target.value)} style={{ ...SEL, minWidth: 130, width: "100%" }}>
                        {a.opcoes.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                    </td>
                  ))}
                </tr>
              );
              return linhas;
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10, lineHeight: 1.5 }}>
        "Ligados a negócios em curso" = negócios com CPCV pedido, CPCV assinado ou escritura agendada. "Com financiamento" = negócios com valor de financiamento.
        As partes da líder e da gestora financeira passam a ter valores na app nas etapas 5 e 6.
      </div>
    </div>
  );
}

// ── Comissões: "janela da agência" (quem não vê as partes dos consultores) ──
const eur = v => (v || v === 0) && v !== "" ? Number(v).toLocaleString("pt-PT", { maximumFractionDigits: 0 }) + " €" : "—";
const dt = d => d ? new Date(d).toLocaleDateString("pt-PT") : "—";
export function ComissoesAgencia({ supabase }) {
  const [linhas, setLinhas] = useState(null);
  useEffect(() => {
    supabase.rpc("comissoes_agencia").then(({ data, error }) => {
      if (error) { alert("Erro a carregar comissões: " + error.message); setLinhas([]); return; }
      setLinhas(data || []);
    });
  }, [supabase]);
  if (!linhas) return <div style={{ padding: 30, color: "#9ca3af" }}>A carregar...</div>;
  const total = linhas.reduce((s, l) => s + Number(l.comissao_total || 0), 0);
  const pago = linhas.reduce((s, l) => s + Number(l.fase1_pago_cliente || 0) + Number(l.fase2_pago_cliente || 0), 0);
  const TH = { textAlign: "left", padding: "8px", borderBottom: "2px solid #0B1B3D", fontSize: 11.5, whiteSpace: "nowrap" };
  const TD = { padding: "7px 8px", borderBottom: "1px solid #f3f4f6", fontSize: 12, whiteSpace: "nowrap" };
  return (
    <div style={{ padding: "24px 0" }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, marginBottom: 4 }}>Comissões da agência</h1>
      <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 16 }}>🔒 Só leitura · lado da agência (valor cobrado ao cliente, fases e faturas). As partes dos consultores não são mostradas neste perfil.</p>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {[["Negócios", linhas.length], ["Comissão total", eur(total)], ["Pago pelos clientes", eur(pago)], ["Por receber", eur(total - pago)]].map(([l, v]) => (
          <div key={l} style={{ background: "#fff", border: "1px solid #ece9e3", borderRadius: 10, padding: "10px 14px", minWidth: 140 }}>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>{l}</div><div style={{ fontWeight: 800, fontSize: 16 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ overflowX: "auto", background: "#fff", borderRadius: 12, border: "1px solid #ece9e3" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            {["Imóvel", "Estado", "Consultores", "Valor", "Comissão", "Fase 1", "Fatura 1", "Pago 1", "Fase 2", "Fatura 2", "Pago 2"].map(h => <th key={h} style={TH}>{h}</th>)}
          </tr></thead>
          <tbody>
            {linhas.map(l => (
              <tr key={l.comissao_id}>
                <td style={TD}>{l.imovel_ref || "—"}</td>
                <td style={TD}>{(l.estado_negocio || "").replace("_", " ")}</td>
                <td style={TD}>{[l.consultor_ang_nome, l.consultor_venda_nome].filter(Boolean).join(" / ") || "—"}</td>
                <td style={TD}>{eur(l.valor_venda)}</td>
                <td style={{ ...TD, fontWeight: 700 }}>{eur(l.comissao_total)}</td>
                <td style={TD}>{eur(l.fase1_valor)}<div style={{ fontSize: 10.5, color: "#9ca3af" }}>{dt(l.fase1_data_prevista)}</div></td>
                <td style={TD}>{l.fase1_fatura || "—"}</td>
                <td style={TD}>{eur(l.fase1_pago_cliente)}<div style={{ fontSize: 10.5, color: "#9ca3af" }}>{dt(l.fase1_data_pago)}</div></td>
                <td style={TD}>{eur(l.fase2_valor)}<div style={{ fontSize: 10.5, color: "#9ca3af" }}>{dt(l.fase2_data_prevista)}</div></td>
                <td style={TD}>{l.fase2_fatura || "—"}</td>
                <td style={TD}>{eur(l.fase2_pago_cliente)}<div style={{ fontSize: 10.5, color: "#9ca3af" }}>{dt(l.fase2_data_pago)}</div></td>
              </tr>
            ))}
            {linhas.length === 0 && <tr><td colSpan={11} style={{ ...TD, textAlign: "center", color: "#9ca3af", padding: 30 }}>Sem comissões para mostrar.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Catálogo "Imóveis da equipa" vive em src/ImoveisEquipa.js
export { CatalogoImoveisEquipa } from "./ImoveisEquipa";

// ── Rede de segurança: quando a base de dados recusa alterar/apagar um registo
// (regras de acesso), o pedido "passa" sem erro mas sem mexer em nada. Este fetch
// pede a resposta de volta e, se vier vazia num pedido a um registo concreto,
// devolve erro e avisa a app (evento "sem-permissao") para mostrar a mensagem.
// Também: qualquer gravação recusada pela base de dados (erro 4xx/5xx) avisa a app (evento "erro-gravar"),
// para nunca falhar em silêncio. Os registos de histórico ficam de fora (são extra e não bloqueiam nada).
const TABELAS_SO_REGISTO = /\/rest\/v1\/(historico|historico_cpcv|historico_negocios|registo_alteracoes)(\?|$)/;
async function avisarErroGravar(resp, url) {
  let mensagem = "";
  try { const j = await resp.clone().json(); mensagem = j?.message || j?.error || ""; } catch (e) { /* resposta sem JSON */ }
  const tabela = decodeURIComponent((url.match(/\/rest\/v1\/([^?/]+)/) || [])[1] || "");
  try { window.dispatchEvent(new CustomEvent("erro-gravar", { detail: { mensagem, tabela, estado: resp.status } })); } catch (e) { /* ignora */ }
}
export function criarFetchComVerificacao(fetchBase = (...a) => fetch(...a)) {
  return async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url || "";
    const metodo = (init.method || "GET").toUpperCase();
    const escrita = /\/rest\/v1\//.test(url) && ["POST", "PATCH", "PUT", "DELETE"].includes(metodo) && !TABELAS_SO_REGISTO.test(url);
    const alvo = /\/rest\/v1\//.test(url) && (metodo === "PATCH" || metodo === "DELETE") && /[?&]id=eq\./.test(url);
    if (!alvo) {
      const resp = await fetchBase(input, init);
      if (escrita && !resp.ok) await avisarErroGravar(resp, url);
      return resp;
    }
    const headers = new Headers(init.headers || {});
    const prefer = headers.get("Prefer") || "";
    if (!/return=representation/.test(prefer)) headers.set("Prefer", [prefer.replace(/return=minimal/, "").replace(/^,|,$/g, ""), "return=representation"].filter(Boolean).join(","));
    const resp = await fetchBase(input, { ...init, headers });
    if (!resp.ok) { if (escrita) await avisarErroGravar(resp, url); return resp; }
    const texto = await resp.clone().text();
    if (texto.trim() === "[]") {
      try { window.dispatchEvent(new CustomEvent("sem-permissao")); } catch (e) { /* ignora */ }
      return new Response(JSON.stringify({ message: "Não tens permissão para alterar este registo.", code: "42501" }), { status: 403, headers: { "Content-Type": "application/json" } });
    }
    return resp;
  };
}

// ── Aplicar as mesmas exceções a várias pessoas de uma vez (Consultores → ações em grupo) ──
export function AplicarExcecoesEmGrupo({ supabase, pessoas, profile, onClose, showNotif }) {
  const [escolhas, setEscolhas] = useState({}); // area → "" (não mudar) | "__perfil" | nível
  const [saving, setSaving] = useState(false);
  const alvo = pessoas.filter(p => p.papel !== "lider" && p.papel !== "admin");
  const nMud = Object.values(escolhas).filter(Boolean).length;

  const aplicar = async () => {
    if (nMud === 0 || alvo.length === 0) return;
    if (!window.confirm(`Aplicar ${nMud} alteraç${nMud === 1 ? "ão" : "ões"} de acesso a ${alvo.length} pessoa(s)?\n\n${alvo.map(p => "• " + p.nome).join("\n")}`)) return;
    setSaving(true);
    const { data: atuais } = await supabase.from("acessos_perfil").select("profile_id, acessos").in("profile_id", alvo.map(p => p.id));
    const mapa = {}; (atuais || []).forEach(r => { mapa[r.profile_id] = r.acessos || {}; });
    const linhas = alvo.map(p => {
      const ex = { ...(mapa[p.id] || {}) };
      Object.entries(escolhas).forEach(([area, v]) => { if (v === "__perfil") delete ex[area]; else if (v) ex[area] = v; });
      return { profile_id: p.id, acessos: ex, atualizado_em: new Date().toISOString(), atualizado_por: profile?.id || null };
    });
    const { error } = await supabase.from("acessos_perfil").upsert(linhas);
    setSaving(false);
    if (error) { alert("Erro a aplicar acessos: " + error.message); return; }
    showNotif(`Acessos aplicados a ${alvo.length} pessoa(s).`);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 22, width: 560, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4 }}>🔐 Aplicar acessos iguais</div>
        <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 12 }}>
          {alvo.length} pessoa(s): {alvo.map(p => p.nome).join(", ") || "—"}
          {pessoas.length > alvo.length && <div style={{ color: "#b45309", marginTop: 4 }}>Líder/Admin selecionados ficam de fora (têm sempre acesso total).</div>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {AREAS_ACESSO.map(a => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: escolhas[a.id] ? 700 : 400, color: escolhas[a.id] ? "#b45309" : "#374151" }}>{a.label}</span>
              <select value={escolhas[a.id] || ""} onChange={e => setEscolhas(p => ({ ...p, [a.id]: e.target.value }))} style={{ ...SEL, borderColor: escolhas[a.id] ? "#f59e0b" : "#ece9e3" }}>
                <option value="">Não mudar</option>
                <option value="__perfil">Igual ao perfil (tirar exceção)</option>
                {a.opcoes.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
          <button onClick={aplicar} disabled={saving || nMud === 0 || alvo.length === 0} style={{ background: nMud && alvo.length ? "#0B1B3D" : "#e5e7eb", color: nMud && alvo.length ? "#fff" : "#9ca3af", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {saving ? "A aplicar..." : `Aplicar a ${alvo.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
